
import React, { useState, useMemo } from 'react';
import { useApp } from '../../../context/AppContext';
import DataTable from '../../../components/DataTable/DataTable';
import StatusBadge from '../../../components/StatusBadge/StatusBadge';
import PageHeader from '../../../components/PageHeader/PageHeader';
import Modal from '../../../components/Modal/Modal';
import Select from '../../../components/Form/Select';
import Input from '../../../components/Form/Input';
import './Orders.css';

const AdminOrders = () => {
    const { orders = {}, pumps = [] } = useApp();   // Safe defaults, dieselorders is object
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [filters, setFilters] = useState({
        city: '',
        pump: '',
        status: '',
        search: '',
    });

    const ordersData = orders || [];
    console.log("orders::::::", ordersData);

    /** ---------------------------
     *  MAPPED ORDERS
     * --------------------------- */
    const mappedOrders = useMemo(() => {
        try {
            if (!Array.isArray(ordersData)) return [];

            return ordersData.map(order => ({
                orderNumber: order.order_id || order._id || 'N/A',
                customerName: order.user?.name || 'N/A',
                city: order.pump?.address?.locality || 'N/A',
                pumpName: order.pump?.brand || 'N/A',
                quantityLiters: order.fuel_quantity || 0,
                amount: order.order_total || 0,
                paymentStatus: order.is_paid ? 'paid' : 'unpaid',
                orderStatus: order.is_completed ? 'completed' : order.is_accepted ? 'accepted' : 'pending',
                createdAt: order.createdAt,
                customerPhone: order.user?.phone || 'N/A',
                vehicleNumber: order.vehicle?.reg_no || 'N/A',
                completedAt: order.updatedAt,
            }));
        } catch (err) {
            console.error("Error mapping orders:", err);
            return [];
        }
    }, [ordersData]);

    /** ---------------------------
     *  SAFE CITY LIST
     * --------------------------- */
    const cities = useMemo(() => {
        try {
            return Array.isArray(mappedOrders)
                ? [...new Set(mappedOrders.map(o => o?.city || ''))]
                      .filter(Boolean)
                      .sort()
                : [];
        } catch (err) {
            console.error("Error loading cities:", err);
            return [];
        }
    }, [mappedOrders]);

    /** ---------------------------
     *  SAFE FILTERED ORDERS
     * --------------------------- */
    const filteredOrders = useMemo(() => {
        try {
            if (!Array.isArray(mappedOrders)) return [];
    
            return mappedOrders.filter(order => {
                if (!order) return false;
    
                // CITY FILTER
                if (filters.city && order.city !== filters.city) return false;
    
                // PUMP FILTER
                if (filters.pump && order.pumpName !== filters.pump) return false;
    
                // STATUS FILTER
                if (filters.status && order.orderStatus !== filters.status) return false;
    
                // SEARCH FILTER (case-insensitive)
                if (filters.search) {
                    const s = filters.search.toLowerCase();
    
                    const searchString =
                        `${order.orderNumber} 
                         ${order.customerName} 
                         ${order.city} 
                         ${order.pumpName} 
                         ${order.customerPhone} 
                         ${order.vehicleNumber}`
                            .toLowerCase();
    
                    if (!searchString.includes(s)) return false;
                }
    
                return true;
            });
        } catch (err) {
            console.error("Error filtering orders:", err);
            return [];
        }
    }, [mappedOrders, filters]);
    

    /** ---------------------------
     *  SAFE DATE FORMATTER
     * --------------------------- */
    const formatDate = (dateString) => {
        try {
            if (!dateString) return 'N/A';
            return new Date(dateString).toLocaleString('en-IN');
        } catch {
            return 'N/A';
        }
    };

    /** ---------------------------
     *  TABLE COLUMNS WITH SAFE RENDERING
     * --------------------------- */
    const columns = [
        { key: 'orderNumber', label: 'Order #', width: '150px' },
        { key: 'customerName', label: 'Customer' },
        { key: 'city', label: 'City' },
        { key: 'pumpName', label: 'Pump' },
        {
            key: 'quantityLiters',
            label: 'Quantity (L)',
            render: (value) => `${value || 0} L`,
        },
        {
            key: 'amount',
            label: 'Amount',
            render: (value) => {
                try {
                    return `₹${(value || 0).toLocaleString('en-IN')}`;
                } catch {
                    return `₹0`;
                }
            },
        },
        {
            key: 'paymentStatus',
            label: 'Payment',
            render: (value) => <StatusBadge status={value || 'unknown'} type="payment" />,
        },
        {
            key: 'orderStatus',
            label: 'Status',
            render: (value) => <StatusBadge status={value || 'unknown'} type="order" />,
        },
        {
            key: 'createdAt',
            label: 'Created',
            render: (value) => formatDate(value),
        },
    ];



    /** ---------------------------
     *  HANDLE ROW CLICK
     * --------------------------- */
    const handleRowClick = (order) => {
        setSelectedOrder(order);
        setIsModalOpen(true);
    };

    return (
        <div className="admin-orders">
            <PageHeader title="Orders Management" />



            {/* ---------------------------
                FILTERS
            --------------------------- */}
            <div className="orders-filters">
               

                <Select
                    label="Filter by City"
                    value={filters.city}
                    onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                    options={[
                        { value: '', label: 'All Cities' },
                        ...cities.map(city => ({ value: city, label: city })),
                    ]}
                />

                <Select
                    label="Filter by Pump"
                    value={filters.pump}
                    onChange={(e) => setFilters({ ...filters, pump: e.target.value })}
                    options={[
                        { value: '', label: 'All Pumps' },
                        ...(Array.isArray(pumps)
                            ? pumps.map(p => ({
                                  value: p?.brand || '',
                                  label: p?.brand || 'Unknown Pump',
                              }))
                            : [])
                    ]}
                />

                <Select
                    label="Filter by Status"
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    options={[
                        { value: '', label: 'All Statuses' },
                        { value: 'pending', label: 'Pending' },
                        { value: 'accepted', label: 'Accepted' },
                        { value: 'completed', label: 'Completed' },
                        { value: 'cancelled', label: 'Cancelled' },
                        { value: 'refunded', label: 'Refunded' },
                    ]}
                />

                <Input
                    label="Search Orders"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    placeholder="Search"
                />

            </div>

            {/* ---------------------------
                TABLE
            --------------------------- */}
         
            <DataTable
                columns={columns}
                data={filteredOrders || []}
                onRowClick={handleRowClick}
            />

            {/* ---------------------------
                MODAL
            --------------------------- */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={`Order Details - ${selectedOrder?.orderNumber || ''}`}
                size="large"
            >
                {!selectedOrder ? (
                    <p style={{ padding: '1rem' }}>No order selected.</p>
                ) : (
                    <div className="order-details">

                        {/* CUSTOMER INFO */}
                        <div className="order-details-section">
                            <h3>Customer Information</h3>
                            <div className="order-details-grid">
                                <div><label>Name</label><p>{selectedOrder.customerName || 'N/A'}</p></div>
                                <div><label>Phone</label><p>{selectedOrder.customerPhone || 'N/A'}</p></div>
                                <div><label>City</label><p>{selectedOrder.city || 'N/A'}</p></div>
                                <div><label>Vehicle Number</label><p>{selectedOrder.vehicleNumber || 'N/A'}</p></div>
                            </div>
                        </div>

                        {/* ORDER INFO */}
                        <div className="order-details-section">
                            <h3>Order Information</h3>
                            <div className="order-details-grid">
                                <div><label>Pump Name</label><p>{selectedOrder.pumpName || 'N/A'}</p></div>
                                <div><label>Quantity</label><p>{selectedOrder.quantityLiters || 0} Liters</p></div>
                                <div>
                                    <label>Amount</label>
                                    <p>₹{selectedOrder?.amount?.toLocaleString?.('en-IN') || '0'}</p>
                                </div>
                                <div>
                                    <label>Payment Status</label>
                                    <p><StatusBadge status={selectedOrder.paymentStatus} type="payment" /></p>
                                </div>
                                <div>
                                    <label>Order Status</label>
                                    <p><StatusBadge status={selectedOrder.orderStatus} type="order" /></p>
                                </div>
                            </div>
                        </div>

                        {/* TIMELINE */}
                        <div className="order-details-section">
                            <h3>Timeline</h3>
                            <div className="order-timeline">
                                <div className="timeline-item">
                                    <span className="timeline-label">Created</span>
                                    <span className="timeline-value">{formatDate(selectedOrder.createdAt)}</span>
                                </div>
                                {selectedOrder.completedAt && (
                                    <div className="timeline-item">
                                        <span className="timeline-label">Completed</span>
                                        <span className="timeline-value">{formatDate(selectedOrder.completedAt)}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default AdminOrders;
